import { useEffect, useMemo, useRef } from 'react'

import {
  useGetMemberOnboardingEnrollmentEventsEnrollmentEventIdSmartBudgetRecommendations,
  usePostMemberOnboardingEnrollmentEventsEnrollmentEventIdSmartBudgetCreateOrUpdateRecommendation,
} from '../../../utils/web_api'
import type { MedicalPlanDisplayItem } from '../../member_onboarding_v2/medicalPlanTypes'
import { STRATEGY_ORDER } from '../../member_onboarding_v2/smartBudgetUtils'
import type { BenefitsBundleStrategy, PlansForBenefitsBundle } from '../../member_onboarding_v2/types'

interface UseSavePlanRecommendationsParams {
  enrollmentEventId: string
  smartBudgetData: Record<BenefitsBundleStrategy, PlansForBenefitsBundle> | null
  isLoadingMedicalPlans: boolean
  medicalPlansError: unknown
}

type SavedRecommendations = Partial<Record<BenefitsBundleStrategy, string | null>>

function shouldSendRecommendation (
  newPlanId: string | null,
  existingPlanId: string | null | undefined,
  previouslySentPlanId: string | null
): boolean {
  return !!(
    newPlanId &&
    newPlanId !== existingPlanId &&
    newPlanId !== previouslySentPlanId
  )
}

/**
 * Send a Segment event for a smart budget recommendation
 */
function sendRecommendationEvent (
  strategy: BenefitsBundleStrategy,
  recommendedPlan: MedicalPlanDisplayItem | undefined | null
) {
  if (!recommendedPlan) return

  window.segment?.track('[EE] Smart budget recommendation calculated', {
    strategy,
    medical_plan_id: recommendedPlan.plan.id,
    medical_plan_name: recommendedPlan.plan.display_name,
    medical_plan_carrier: recommendedPlan.plan.carrier_name,
    medical_plan_premium: recommendedPlan.total_premium.formatted_amount,
    medical_plan_actuarial_value: recommendedPlan.plan.computed_actuarial_value,
    medical_plan_metal_tier: recommendedPlan.plan.metal_tier,
  })
}

/**
 * Hook that fetches existing recommendations and saves new ones when they differ.
 * Sends both Segment events and backend API calls when recommendations change.
 *
 * This hook ensures:
 * - Save only happens after load recommendations query finishes successfully
 * - Save only happens after medical plans load successfully
 * - Only sends recommendations that have changed
 * - Runs only once after both queries have completed successfully
 */
export function useSavePlanRecommendations ({
  enrollmentEventId,
  smartBudgetData,
  isLoadingMedicalPlans,
  medicalPlansError,
}: UseSavePlanRecommendationsParams) {
  const {
    data: existingRecommendations,
    isLoading: isLoadingRecommendations,
    isError: isRecommendationsError,
    isSuccess: isRecommendationsSuccess,
  } = useGetMemberOnboardingEnrollmentEventsEnrollmentEventIdSmartBudgetRecommendations(
    enrollmentEventId,
    {
      query: {
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        staleTime: 0, // Always consider data stale to ensure fresh data on page load
      },
    }
  )

  const { mutate: saveRecommendation } = usePostMemberOnboardingEnrollmentEventsEnrollmentEventIdSmartBudgetCreateOrUpdateRecommendation()

  // Track sent recommendations per strategy to avoid duplicate sends
  const sentRecommendationsRef = useRef<Record<BenefitsBundleStrategy, string | null>>(
    Object.fromEntries(
      STRATEGY_ORDER.map(strategy => [strategy, null])
    ) as Record<BenefitsBundleStrategy, string | null>
  )

  // Track state per enrollment event to allow reset when enrollment event changes
  const enrollmentStateRef = useRef<{
    enrollmentEventId: string | null
    hasRunOnce: boolean
  }>({
    enrollmentEventId: null,
    hasRunOnce: false,
  })

  // Check if all prerequisites are met to save recommendations
  const isReadyToSave = useMemo(() => {
    return (
      !isLoadingMedicalPlans &&
      !medicalPlansError &&
      !isLoadingRecommendations &&
      !isRecommendationsError &&
      isRecommendationsSuccess &&
      !!smartBudgetData
    )
  }, [
    isLoadingMedicalPlans,
    medicalPlansError,
    isLoadingRecommendations,
    isRecommendationsError,
    isRecommendationsSuccess,
    smartBudgetData,
  ])

  // Reset state when enrollment event ID changes
  useEffect(() => {
    if (
      enrollmentStateRef.current.enrollmentEventId !== null &&
      enrollmentStateRef.current.enrollmentEventId !== enrollmentEventId
    ) {
      // Reset state for new enrollment event
      enrollmentStateRef.current.hasRunOnce = false
      enrollmentStateRef.current.enrollmentEventId = enrollmentEventId
      // Reset sent recommendations tracking
      sentRecommendationsRef.current = Object.fromEntries(
        STRATEGY_ORDER.map(strategy => [strategy, null])
      ) as Record<BenefitsBundleStrategy, string | null>
    } else if (enrollmentStateRef.current.enrollmentEventId === null) {
      // Initialize on first mount
      enrollmentStateRef.current.enrollmentEventId = enrollmentEventId
    }
  }, [enrollmentEventId])

  useEffect(() => {
    if (!isReadyToSave) return
    if (enrollmentStateRef.current.hasRunOnce) return

    // smartBudgetData is guaranteed to be non-null when isReadyToSave is true
    const savedRecommendations = existingRecommendations?.smart_budget_recommendations as SavedRecommendations | undefined

    STRATEGY_ORDER.forEach((strategy) => {
      const recommendedPlan = smartBudgetData![strategy]?.recommended_plan
      const newPlanId = recommendedPlan?.plan.id || null

      if (!shouldSendRecommendation(newPlanId, savedRecommendations?.[strategy], sentRecommendationsRef.current[strategy])) {
        return
      }

      // Send Segment event (fire and forget)
      sendRecommendationEvent(strategy, recommendedPlan)

      // Save to backend
      saveRecommendation(
        {
          enrollmentEventId,
          data: {
            strategy,
            medical_plan_id: newPlanId ?? undefined,
          },
        },
        {
          onSuccess: () => {
            sentRecommendationsRef.current[strategy] = newPlanId
          },
        }
      )
    })

    // Mark that we've run once for this enrollment event
    enrollmentStateRef.current.hasRunOnce = true
  }, [
    isReadyToSave,
    smartBudgetData,
    existingRecommendations,
    enrollmentEventId,
    saveRecommendation,
  ])
}
